import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCompare } from "../context/CompareContext";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getGOPENSCutoffsByCollegeId } from "./CollegeDetail";
import { useSearchModal } from "../context/SearchModalContext";

/* -------------------- STYLES -------------------- */

const BORDER = "#90a4ae";

const headerCell = {
  border: `1px solid ${BORDER}`,
  fontWeight: "bold",
  fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
  textAlign: "center",
  verticalAlign: "middle",
  wordBreak: "break-word",
  backgroundColor: "#f8f9fa",
  p: { xs: 0.5, sm: 1 },
};

const featureCell = {
  border: `1px solid ${BORDER}`,
  fontWeight: "bold",
  fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
  textAlign: "left",
  wordBreak: "break-word",
  backgroundColor: "#fafafa",
  width: "30%",
  p: { xs: 0.5, sm: 1 },
};

const valueCell = {
  border: `1px solid ${BORDER}`,
  fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
  textAlign: "center",
  wordBreak: "break-word",
  p: { xs: 0.5, sm: 1 },
};

const collegeNameStyle = {
  fontWeight: "bold",
  fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
  lineHeight: 1.3,
};

/* -------------------- COMPONENT -------------------- */

const ComparisonPage = () => {
  const navigate = useNavigate();
  const { colleges, clearCompare } = useCompare();
  const { openSearch } = useSearchModal();

  const getHighestCutoff = (collegeId) => {
    const data = getGOPENSCutoffsByCollegeId(collegeId);
    return data?.length ? data[0] : null;
  };

  const formatFee = (fee) =>
    fee && fee !== "N/A" ? `₹${fee}` : "N/A";

  const rows = useMemo(
    () => [
      { label: "University", key: "university" },
      { label: "Autonomy Status", key: "autonomyStatus" },
      { label: "District", key: "district" },
      { label: "Total Fee", get: (c) => formatFee(c.fee) },
      { label: "Total Intake", key: "intake" },
      {
        label: "Highest Cutoff Branch",
        get: (c) => getHighestCutoff(c.id)?.courseName || "N/A",
      },
      {
        label: "Highest Rank",
        get: (c) => getHighestCutoff(c.id)?.rank || "N/A",
      },
      {
        label: "Highest Percentile",
        get: (c) =>
          getHighestCutoff(c.id)
            ? `${getHighestCutoff(c.id).percentile}%`
            : "N/A",
      },
      {
        label: "Official Website",
        get: (c) =>
          c.webAddress ? (
            <Typography
              sx={{
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: { xs: "0.6rem", sm: "0.75rem" },
              }}
              onClick={() => {
                let url = c.webAddress.trim();
                if (!/^https?:\/\//i.test(url)) url = "http://" + url;
                window.open(url, "_blank", "noopener");
              }}
            >
              Visit Website
            </Typography>
          ) : (
            "N/A"
          ),
      },
    ],
    []
  );

  if (colleges.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>No colleges added for comparison</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f5f7fb", minHeight: "100vh", p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton
          onClick={() => {
            clearCompare();
            openSearch();
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography sx={{ fontWeight: "bold", ml: 1 }}>
          College Comparison
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table
          sx={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headerCell,
                  position: "relative",
                  p: 0,
                  height: { xs: 70, sm: 90 },
                }}
              >
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    left: 8,
                    fontSize: { xs: "0.6rem", sm: "0.75rem" },
                    fontWeight: 600,
                  }}
                >
                  Features
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 8,
                    fontSize: { xs: "0.6rem", sm: "0.75rem" },
                    fontWeight: 600,
                  }}
                >
                  College Name
                </Typography>
              </TableCell>

              {colleges.map((c) => (
                <TableCell key={c.id} sx={headerCell}>
                  <Typography sx={collegeNameStyle}>{c.name}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={featureCell}>{row.label}</TableCell>

                {colleges.map((c) => (
                  <TableCell key={c.id} sx={valueCell}>
                    {row.get ? row.get(c) : c[row.key] || "N/A"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Disclaimer */}
      <Paper
        sx={{
          mt: 2,
          p: { xs: 1.5, sm: 2 },
          backgroundColor: "#fff3cd",
          border: "1px solid #ffeeba",
        }}
      >
        <Typography sx={{ color: "#d32f2f", fontWeight: "bold", mb: 1 }}>
          Disclaimer:
        </Typography>

        <Divider sx={{ mb: 1 }} />

        <Typography
  sx={{
    fontSize: { xs: "0.50rem", sm: "0.75rem", md: "0.8rem" },
    lineHeight: 1.5
  }}
>
          The comparison data shown above is prepared using publicly available
          information from official counselling records and previous years’
          cutoff data. Actual cutoffs, fees, intake, and admission details may
          vary every year. This comparison is for reference only.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ComparisonPage;